import { Box, Typography, Button, Tooltip, IconButton, Badge, Chip } from '@mui/material';
import { theme } from 'config/theme';
import AddLineIcon from 'remixicon-react/AddLineIcon';
import Filter3LineIcon from 'remixicon-react/Filter3LineIcon';
import CloseCircleFillIcon from 'remixicon-react/CloseCircleFillIcon';
import { useTrackerContext } from '../../context';
import { useTrackerDetailsQuery } from '../../queries';
import Search from './Search';
import { styled } from '@mui/material/styles';
import { useState } from 'react';
import useUserData from 'hooks/useUserData';
import useTrackerStore from '@buildwise/libs/trackers/store/useTrackerStore';
import TrackerFiltersModal from './TrackerFiltersModal';
import { useConfigQuery } from '@buildwise/libs/configurations';
import useLayoutStore from 'store/useLayoutStore';

function TrackerHeader() {
  const {
    state: { trackerId },
    dispatch,
  } = useTrackerContext();
  const { tracker } = useTrackerDetailsQuery({
    trackerId,
    shouldFetch: false,
  });
  const [openFiltersModal, setOpenFiltersModal] = useState(false);
  const { companyId } = useUserData();
  const { trackerState, resetTrackerState } = useTrackerStore();
  const { isFullScreen } = useLayoutStore();
  const selectedFilters = trackerState?.[companyId]?.[trackerId]?.filters || [];

  const { activeUniqueEntries } = useConfigQuery({
    companyId,
    configName: tracker?.configNameForFilters,
  });

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent={isFullScreen ? 'flex-start' : 'space-between'}
      pb={1}
    >
      {!isFullScreen && (
        <Typography
          variant="h4"
          sx={(theme) => ({
            color: theme.palette.custom.tuna,
            fontWeight: 700,
            fontSize: 20,
          })}
        >
          {tracker?.name}
        </Typography>
      )}
      <Box display="flex" alignItems="center" gap={isFullScreen ? 2 : 1}>
        <Button
          variant="outlined"
          startIcon={<AddLineIcon />}
          onClick={() => {
            dispatch(({ state }) => ({
              payload: {
                ...state,
                isOpenedTaskForm: true,
                selectedTask: undefined,
              },
            }));
          }}
          sx={(theme) => ({
            backgroundColor: theme.palette.custom.white,
          })}
        >
          Add Task
        </Button>
        <Search />
        {activeUniqueEntries?.length > 0 && (
          <StyledChip
            label={
              <Box display="flex" alignItems="center">
                <Filter3LineIcon size={20} />
                &nbsp;Filters
                {selectedFilters.length > 0 && (
                  <>
                    &nbsp;({selectedFilters.length})
                    <IconButton
                      size="small"
                      sx={(theme) => ({
                        ml: 0.5,
                        color: theme.palette.custom.pinkSwan,
                        '&:hover': { color: 'inherit' },
                      })}
                      onClick={(event) => {
                        event.stopPropagation();
                        resetTrackerState(companyId, trackerId);
                      }}
                      title="Clear Filters"
                    >
                      <CloseCircleFillIcon size={20} color={theme.palette.custom.pirateGold} />
                    </IconButton>
                  </>
                )}
              </Box>
            }
            variant="outlined"
            onClick={() => setOpenFiltersModal(true)}
            sx={(theme) => ({
              borderColor: 'transparent',
              ...(selectedFilters.length > 0 && {
                backgroundColor: theme.palette.custom.earlyDawn,
                color: theme.palette.custom.brown,
                '&:hover': {
                  borderColor: theme.palette.custom.paleGold,
                  backgroundColor: `${theme.palette.custom.earlyDawn} !important`,
                },
              }),
            })}
          />
        )}
        {openFiltersModal && (
          <TrackerFiltersModal open={openFiltersModal} setOpen={setOpenFiltersModal} />
        )}
      </Box>
    </Box>
  );
}

const StyledChip = styled(Chip, { label: 'Chip' })(({ theme }) => ({
  cursor: 'pointer',
  padding: '20px 4px',
  textTransform: 'capitalize',
  backgroundColor: theme.palette.custom.white,
}));

export default TrackerHeader;
